/* eslint-disable @typescript-eslint/no-explicit-any */
import { Ban, ChevronDown, Pipette, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { Segmented, Switch } from "../ds";
import { SWATCHES } from "./catalog";

/* Building blocks of the Format pane: Power BI-style collapsible sections
 * and labelled controls that always show the value in effect (the default,
 * greyed, when nothing is set). */

// ---------- immutable path helpers ----------

export function getIn(obj: any, path: string[]): any {
  let cur = obj;
  for (const k of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[k];
  }
  return cur;
}

/** Returns a copy of `obj` with `path` set. `undefined` removes the key and
 * prunes objects left empty, so "reset" leaves no `{}` litter behind. */
export function setIn(obj: any, path: string[], value: any): any {
  const [k, ...rest] = path;
  const base = obj && typeof obj === "object" && !Array.isArray(obj) ? { ...obj } : {};
  if (!rest.length) {
    if (value === undefined) delete base[k];
    else base[k] = value;
  } else {
    const child = setIn(base[k], rest, value);
    if (child === undefined || (typeof child === "object" && !Array.isArray(child) && !Object.keys(child).length)) delete base[k];
    else base[k] = child;
  }
  return base;
}

// ---------- section ----------

const OPEN_KEY = "mrp.studio.formatOpen";
function readOpen(): Record<string, boolean> {
  try {
    return JSON.parse(window.localStorage.getItem(OPEN_KEY) || "{}");
  } catch {
    return {};
  }
}

export function Section({ id, title, icon, summary, defaultOpen, onReset, canReset, children }: { id: string; title: string; icon?: ReactNode; summary?: ReactNode; defaultOpen?: boolean; onReset?: () => void; canReset?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState<boolean>(() => readOpen()[id] ?? !!defaultOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(OPEN_KEY, JSON.stringify({ ...readOpen(), [id]: next }));
    } catch {
      /* ignore */
    }
  };
  return (
    <section className={`st-fsec${open ? " is-open" : ""}`}>
      <div className="st-fsec__head">
        <button type="button" className="st-fsec__toggle" aria-expanded={open} onClick={toggle}>
          <ChevronDown size={15} className="st-fsec__chev" />
          {icon}
          <span className="st-fsec__title">{title}</span>
          {!open && summary ? <span className="st-fsec__summary">{summary}</span> : null}
        </button>
        {onReset && canReset ? (
          <button type="button" className="st-fsec__reset" onClick={onReset} title={`Reset ${title.toLowerCase()} to the Look's defaults`}>
            <RotateCcw size={12} /> Reset
          </button>
        ) : null}
      </div>
      {open ? <div className="st-fsec__body">{children}</div> : null}
    </section>
  );
}

// ---------- rows ----------

export function Row({ label, hint, children, stacked }: { label: ReactNode; hint?: ReactNode; children: ReactNode; stacked?: boolean }) {
  return (
    <div className={`st-frow${stacked ? " st-frow--stacked" : ""}`}>
      <div className="st-frow__label">
        {label}
        {hint ? <span className="st-frow__hint">{hint}</span> : null}
      </div>
      <div className="st-frow__ctl">{children}</div>
    </div>
  );
}

export function ToggleRow({ label, hint, checked, onChange }: { label: ReactNode; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="st-frow st-frow--toggle">
      <div className="st-frow__label">
        {label}
        {hint ? <span className="st-frow__hint">{hint}</span> : null}
      </div>
      <Switch checked={checked} onChange={onChange} />
    </div>
  );
}

export function SegRow<T extends string>({ label, hint, value, options, onChange }: { label: ReactNode; hint?: ReactNode; value: T; options: Array<{ value: T; label: ReactNode; title?: string }>; onChange: (v: T) => void }) {
  return (
    <Row label={label} hint={hint} stacked>
      <div className="st-seg-fill">
        <Segmented size="sm" value={value} options={options} onChange={onChange} />
      </div>
    </Row>
  );
}

/** Range + number box. `value` undefined = using the default (shown greyed). */
export function SliderRow({ label, hint, value, defaultValue, min, max, step = 1, unit, onChange, format }: { label: ReactNode; hint?: ReactNode; value: number | undefined | null; defaultValue: number; min: number; max: number; step?: number; unit?: string; onChange: (v: number | undefined) => void; format?: (n: number) => string }) {
  const isSet = value !== undefined && value !== null;
  const shown = isSet ? (value as number) : defaultValue;
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const [text, setText] = useState(String(shown));
  useEffect(() => setText(format ? format(shown) : String(shown)), [shown]); // eslint-disable-line
  return (
    <Row label={label} hint={hint} stacked>
      <div className={`st-slider${isSet ? "" : " is-default"}`}>
        <input type="range" min={min} max={max} step={step} value={shown} onChange={(e) => onChange(clamp(Number(e.target.value)))} aria-label={typeof label === "string" ? label : undefined} />
        <input
          className="st-slider__num"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const n = parseFloat(text);
            if (Number.isFinite(n)) onChange(clamp(n));
            else setText(String(shown));
          }}
          onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
          aria-label={`${typeof label === "string" ? label : "Value"} (number)`}
        />
        {unit ? <span className="st-slider__unit">{unit}</span> : null}
        {isSet ? (
          <button type="button" className="st-mini" title="Back to default" aria-label="Back to default" onClick={() => onChange(undefined)}>
            <RotateCcw size={11} />
          </button>
        ) : (
          <span className="st-slider__auto" title="Using the default">auto</span>
        )}
      </div>
    </Row>
  );
}

/** Optional number (e.g. axis min/max). Empty = automatic. */
export function NumberRow({ label, hint, value, placeholder = "Auto", onChange }: { label: ReactNode; hint?: ReactNode; value: number | null | undefined; placeholder?: string; onChange: (v: number | null) => void }) {
  const [text, setText] = useState(value == null ? "" : String(value));
  useEffect(() => setText(value == null ? "" : String(value)), [value]);
  return (
    <Row label={label} hint={hint}>
      <input
        className="ds-input ds-input--sm st-numbox"
        value={text}
        placeholder={placeholder}
        inputMode="decimal"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const t = text.trim();
          if (!t) return onChange(null);
          const n = parseFloat(t);
          if (Number.isFinite(n)) onChange(n);
          else setText(value == null ? "" : String(value));
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
    </Row>
  );
}

// ---------- colour ----------

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function ColorRow({ label, hint, value, onChange, allowTransparent, autoLabel = "Auto" }: { label: ReactNode; hint?: ReactNode; value: string | undefined | null; onChange: (v: string | undefined) => void; allowTransparent?: boolean; autoLabel?: string }) {
  return (
    <Row label={label} hint={hint}>
      <ColorButton value={value} onChange={onChange} allowTransparent={allowTransparent} autoLabel={autoLabel} />
    </Row>
  );
}

export function ColorButton({ value, onChange, allowTransparent, autoLabel = "Auto", compact }: { value: string | undefined | null; onChange: (v: string | undefined) => void; allowTransparent?: boolean; autoLabel?: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value ?? "");
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => setHex(value && value !== "transparent" ? value : ""), [value, open]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc, true);
    };
  }, [open]);
  const pick = (c: string | undefined) => {
    onChange(c);
    setOpen(false);
  };
  const isAuto = !value && autoLabel !== "None";
  const isClear = value === "transparent" || (!value && autoLabel === "None");
  return (
    <div className="st-color" ref={wrap}>
      <button type="button" className={`st-color__btn${compact ? " is-compact" : ""}`} onClick={() => setOpen((o) => !o)} aria-haspopup="dialog" aria-expanded={open} title={isAuto ? `${autoLabel} (from the Look)` : value ?? ""}>
        <span className={`st-color__chip${isAuto ? " is-auto" : ""}${isClear ? " is-clear" : ""}`} style={!isAuto && !isClear ? { background: value! } : undefined} />
        {!compact ? <span className="st-color__val">{isAuto ? autoLabel : isClear ? "None" : value}</span> : null}
        {!compact ? <ChevronDown size={13} className="ds-faint" /> : null}
      </button>
      {open ? (
        <div className="st-color__pop" role="dialog" aria-label="Pick a colour">
          <div className="st-color__grid">
            {SWATCHES.map((c) => (
              <button key={c} type="button" className={`st-color__sw${value?.toLowerCase() === c ? " is-on" : ""}`} style={{ background: c }} title={c} aria-label={c} onClick={() => pick(c)} />
            ))}
          </div>
          <div className="st-color__custom">
            <label className="st-color__native" title="Pick any colour">
              <Pipette size={13} />
              <input type="color" value={HEX.test(hex) && hex.length === 7 ? hex : "#0f6cbd"} onChange={(e) => { setHex(e.target.value); onChange(e.target.value); }} />
            </label>
            <input
              className="ds-input ds-input--sm"
              value={hex}
              placeholder="#1a2b3c"
              onChange={(e) => setHex(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = hex.startsWith("#") ? hex : `#${hex}`;
                  if (HEX.test(v)) pick(v.toLowerCase());
                }
              }}
              aria-label="Custom colour (hex)"
            />
            <button type="button" className="ds-btn ds-btn--sm" onClick={() => { const v = hex.startsWith("#") ? hex : `#${hex}`; if (HEX.test(v)) pick(v.toLowerCase()); }} disabled={!HEX.test(hex.startsWith("#") ? hex : `#${hex}`)}>
              Use
            </button>
          </div>
          <div className="st-color__foot">
            <button type="button" className="st-link-btn" onClick={() => pick(undefined)}>
              <RotateCcw size={12} /> {autoLabel} (from the Look)
            </button>
            {allowTransparent ? (
              <button type="button" className="st-link-btn" onClick={() => pick("transparent")}>
                <Ban size={12} /> None
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Scope for "apply to all" patches. */
export type ApplyScope = "page" | "report";
/** Write `value` at display.<path> on every matching widget of the page / report.
 * A plain-object `value` is MERGED into the existing object at `path`
 * (undefined-valued keys delete that key); anything else replaces. */
export type ApplyDisplayPatch = (path: string[], value: unknown, scope: ApplyScope, onlyTypes?: string[]) => void;

/** "Apply to every <thing> on this page / in this report" button pair. */
export function ApplyAllRow({ what, onApply }: { what: string; onApply: (scope: ApplyScope) => void }) {
  return (
    <div className="st-frow-pair" style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "4px 0 8px" }}>
      <button type="button" className="st-arr__seg" onClick={() => onApply("page")}>Apply to every {what} on this page</button>
      <button type="button" className="st-arr__seg" onClick={() => onApply("report")}>Apply to every {what} in this report</button>
    </div>
  );
}
